// Tipos, catálogos y helpers compartidos del módulo Usuarios: una sola fuente de
// verdad para que "Crear usuario" (vista independiente) y "Editar usuario" (modal)
// no diverjan en campos ni validaciones.
import { avisoExito, avisoInfo } from '@/lib/avisoCentral';

export const TIPOS_DOC = ['CC', 'CE', 'TI', 'NIT', 'PASAPORTE'];

// Área/dependencia (antes "módulo"): las tres dependencias reales del proceso.
export const AREAS: Array<{ v: string; label: string }> = [
  { v: 'INSTRUCCION', label: 'Instrucción' },
  { v: 'JUZGAMIENTO', label: 'Juzgamiento' },
  { v: 'SUPERIOR', label: 'Superior (Gobernador)' },
];

export const ESTADOS = ['ACTIVO', 'INACTIVO', 'SUSPENDIDO'] as const;
export type Estado = (typeof ESTADOS)[number];

export const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Celular colombiano: exactamente 10 dígitos y empieza por 3.
export const RE_TELEFONO = /^3\d{9}$/;

export function labelArea(v?: string | null): string {
  return AREAS.find((a) => a.v === v)?.label ?? '—';
}

// UsuarioSeguro del backend (snake_case).
export interface Usuario {
  id: string;
  nombre: string;
  apellido: string;
  tipo_documento: string;
  numero_documento: string;
  telefono?: string | null;
  correo: string;
  rol: string;
  area?: string | null;
  es_funcionario?: boolean;
  numero_contrato?: string | null;
  fecha_inicio_contrato?: string | null;
  fecha_terminacion_contrato?: string | null;
  plazo_meses?: number | null;
  plazo_dias?: number | null;
  tiene_adicion?: boolean;
  fecha_terminacion_adicion?: string | null;
  adicion_meses?: number | null;
  adicion_dias?: number | null;
  firma_key?: string | null;
  estado: Estado;
  // Trazabilidad del registro (pedido del cliente ago-06): quién lo creó y quién
  // lo editó por última vez, con fecha y hora.
  creado_por_nombre?: string | null;
  fecha_creacion: string;
  modificado_por_nombre?: string | null;
  fecha_modificacion?: string | null;
}

/** Fecha y hora legibles para la trazabilidad: "dd/mm/aaaa, hh:mm". */
export function fechaHoraTraza(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('es-CO')}, ${d.toLocaleTimeString('es-CO', {
    hour: '2-digit', minute: '2-digit',
  })}`;
}

// Roles que son FUNCIONARIOS por definición (directores y funcionarios de
// planta): a ellos nunca se les pide contrato. Otros roles pueden serlo o no,
// y lo dicen con la opción «¿Es funcionario?» del formulario.
export const ROLES_SIN_CONTRATO = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'FUNCIONARIO_INSTRUCCION',
  'FUNCIONARIO_JUZGAMIENTO',
];

/** ¿El rol es funcionario/director de planta (sin contrato) por definición? */
export function rolSinContrato(rol: string): boolean {
  return ROLES_SIN_CONTRATO.includes(rol);
}

// Roles a los que NO se les captura la sección funcionario/contrato (decidido
// con el cliente ago-05): ni la pregunta «¿Es funcionario?», ni número/fechas/
// plazo/adición del contrato, ni el estado inicial. Se guardan SIN datos de
// contrato (el DTO viaja por el mismo camino "sin contrato" que la planta).
export const ROLES_SIN_SECCION_CONTRATO = ['SUPERVISOR'];

/** ¿Al rol no se le captura la sección de contrato (ni estado inicial)? */
export function rolSinSeccionContrato(rol: string): boolean {
  return ROLES_SIN_SECCION_CONTRATO.includes(rol);
}

// Roles que trabajan SIEMPRE por contrato (decidido con el cliente ago-05): la
// pregunta «¿Es funcionario?» no se muestra y los datos del contrato (número,
// fechas, plazo, adición) se piden directamente. Lo demás no cambia.
export const ROLES_SIEMPRE_CONTRATO = ['CONTRATISTA_INSTRUCCION'];

/** ¿El rol es SIEMPRE por contrato (sin la pregunta «¿Es funcionario?»)? */
export function rolSiempreContrato(rol: string): boolean {
  return ROLES_SIEMPRE_CONTRATO.includes(rol);
}

// Formulario de creación/edición (DTO camelCase). `esFuncionario` marca que la
// persona es funcionaria de planta (no trabaja por contrato): se ocultan y
// omiten número, fechas, plazo y adición. El plazo del contrato se DIGITA en
// meses y días (sin cálculo automático; decidido con el cliente). `password`
// es la contraseña INICIAL asignada por quien crea (solo aplica en el alta).
export interface FormUsuario {
  nombre: string; apellido: string; tipoDocumento: string; numeroDocumento: string;
  telefono: string; correo: string; password: string; rol: string; area: string;
  esFuncionario: boolean;
  numeroContrato: string;
  fechaInicioContrato: string; fechaTerminacionContrato: string;
  plazoMeses: string; plazoDias: string;
  tieneAdicion: boolean; fechaTerminacionAdicion: string;
  adicionMeses: string; adicionDias: string;
  estado: 'ACTIVO' | 'INACTIVO';
}

export const FORM_VACIO: FormUsuario = {
  nombre: '', apellido: '', tipoDocumento: 'CC', numeroDocumento: '',
  telefono: '', correo: '', password: '', rol: 'FUNCIONARIO_INSTRUCCION', area: 'INSTRUCCION',
  esFuncionario: false, numeroContrato: '',
  fechaInicioContrato: '', fechaTerminacionContrato: '',
  plazoMeses: '', plazoDias: '',
  tieneAdicion: false, fechaTerminacionAdicion: '',
  adicionMeses: '', adicionDias: '',
  estado: 'ACTIVO',
};

/** ¿Al formulario le aplica contrato? (ni funcionario marcado ni rol de planta). */
export function contratoAplica(f: Pick<FormUsuario, 'esFuncionario' | 'rol'>): boolean {
  // Al supervisor no se le captura la sección de contrato.
  if (rolSinSeccionContrato(f.rol)) return false;
  // El contratista va SIEMPRE por contrato, aunque `esFuncionario` hubiera
  // quedado marcado antes de cambiar el rol en el formulario.
  if (rolSiempreContrato(f.rol)) return true;
  return !f.esFuncionario && !rolSinContrato(f.rol);
}

// Convierte un Usuario (snake_case) al formulario (camelCase) para editar.
export function aFormulario(u: Usuario): FormUsuario {
  const soloFecha = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');
  const num = (v?: number | null) => (v === null || v === undefined ? '' : String(v));
  return {
    nombre: u.nombre, apellido: u.apellido,
    tipoDocumento: u.tipo_documento, numeroDocumento: u.numero_documento,
    telefono: u.telefono ?? '', correo: u.correo, password: '',
    rol: u.rol, area: u.area ?? 'INSTRUCCION',
    // Compatibilidad: los datos viejos marcaban "no aplica" con contrato 'N/A'.
    esFuncionario:
      u.es_funcionario === true || (u.numero_contrato ?? '').trim().toUpperCase() === 'N/A',
    numeroContrato:
      (u.numero_contrato ?? '').trim().toUpperCase() === 'N/A' ? '' : (u.numero_contrato ?? ''),
    fechaInicioContrato: soloFecha(u.fecha_inicio_contrato),
    fechaTerminacionContrato: soloFecha(u.fecha_terminacion_contrato),
    plazoMeses: num(u.plazo_meses), plazoDias: num(u.plazo_dias),
    tieneAdicion: Boolean(u.tiene_adicion),
    fechaTerminacionAdicion: soloFecha(u.fecha_terminacion_adicion),
    adicionMeses: num(u.adicion_meses), adicionDias: num(u.adicion_dias),
    estado: u.estado === 'INACTIVO' ? 'INACTIVO' : 'ACTIVO',
  };
}

/** Entero no negativo desde un input de texto ('' o inválido → 0). */
export function enteroPlazo(s: string): number {
  const v = parseInt(s, 10);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** "X meses y Y días" (null si ambos son 0). */
export function textoPlazo(meses: number, dias: number): string | null {
  const partes: string[] = [];
  if (meses > 0) partes.push(meses === 1 ? '1 mes' : `${meses} meses`);
  if (dias > 0) partes.push(dias === 1 ? '1 día' : `${dias} días`);
  return partes.join(' y ') || null;
}

/**
 * Plazo TOTAL del contrato: suma el plazo digitado + la adición (si la hay),
 * normalizando 30 días = 1 mes para presentar el total.
 */
export function plazoTotalTexto(
  f: Pick<FormUsuario, 'plazoMeses' | 'plazoDias' | 'tieneAdicion' | 'adicionMeses' | 'adicionDias'>,
): string | null {
  let meses = enteroPlazo(f.plazoMeses) + (f.tieneAdicion ? enteroPlazo(f.adicionMeses) : 0);
  let dias = enteroPlazo(f.plazoDias) + (f.tieneAdicion ? enteroPlazo(f.adicionDias) : 0);
  meses += Math.floor(dias / 30);
  dias = dias % 30;
  return textoPlazo(meses, dias);
}

/**
 * Bloque de contrato del DTO (crear/actualizar) a partir del formulario:
 * funcionario → esFuncionario=true y nada más; con contrato → número, fechas,
 * plazo digitado y adición.
 */
export function cuerpoContrato(f: FormUsuario): Record<string, unknown> {
  if (!contratoAplica(f)) return { esFuncionario: true, tieneAdicion: false };
  return {
    esFuncionario: false,
    numeroContrato: f.numeroContrato.trim(),
    fechaInicioContrato: f.fechaInicioContrato || undefined,
    fechaTerminacionContrato: f.fechaTerminacionContrato || undefined,
    plazoMeses: enteroPlazo(f.plazoMeses),
    plazoDias: enteroPlazo(f.plazoDias),
    tieneAdicion: f.tieneAdicion,
    fechaTerminacionAdicion: f.tieneAdicion ? f.fechaTerminacionAdicion || undefined : undefined,
    adicionMeses: f.tieneAdicion ? enteroPlazo(f.adicionMeses) : undefined,
    adicionDias: f.tieneAdicion ? enteroPlazo(f.adicionDias) : undefined,
  };
}

// Respuesta del alta / reenvío de enlace.
export interface RespuestaEnlace {
  correoEnviado: boolean;
  enlaceContrasena?: string;
}

// Muestra el resultado del envío del enlace: si no salió por correo (stub de dev),
// deja el enlace a la vista del administrador para compartirlo.
export function avisarEnlace(r: RespuestaEnlace, base: string): void {
  if (r.correoEnviado) {
    avisoExito(base);
  } else {
    avisoInfo(base, r.enlaceContrasena
        ? `Correo aún no configurado. Enlace para compartir: ${r.enlaceContrasena}`
        : 'Correo aún no configurado.');
  }
}

/**
 * Error por campo (null = correcto). ÚNICA fuente de las reglas del formulario:
 * de aquí salen el borde/mensaje rojo bajo cada campo, el bloqueo del envío y
 * la validación por paso del modal de edición.
 * `conVacios`: si false, solo reporta problemas de FORMATO/coherencia (para la
 * marca en vivo mientras se escribe); los obligatorios vacíos se reportan al
 * intentar enviar. Las fechas ISO (yyyy-mm-dd) se comparan como texto.
 */
export function erroresCamposUsuario(
  f: FormUsuario,
  esCrear: boolean,
  conVacios: boolean,
): Record<string, string | null> {
  const vacio = (s: string) => !s.trim();
  return {
    nombre: conVacios && vacio(f.nombre) ? 'Ingrese el nombre.' : null,
    apellido: conVacios && vacio(f.apellido) ? 'Ingrese el apellido.' : null,
    numeroDocumento: esCrear && conVacios && vacio(f.numeroDocumento)
      ? 'Ingrese el número de documento.' : null,
    correo: !vacio(f.correo) && !RE_CORREO.test(f.correo.trim())
      ? 'Correo electrónico inválido.'
      : conVacios && vacio(f.correo) ? 'Ingrese el correo.' : null,
    telefono: !vacio(f.telefono) && !RE_TELEFONO.test(f.telefono.trim())
      ? 'Debe tener 10 dígitos y empezar por 3.' : null,
    // Contraseña inicial: solo en el alta (la asigna quien crea; sin trim, los
    // espacios son válidos en una contraseña).
    password: !esCrear
      ? null
      : f.password && f.password.length < 8
        ? 'Debe tener al menos 8 caracteres.'
        : conVacios && !f.password
          ? 'Asigne la contraseña inicial.' : null,
    rol: conVacios && !f.rol ? 'Seleccione un rol.' : null,
    area: conVacios && !f.area ? 'Seleccione un área o dependencia.' : null,
    // A los funcionarios (marcados o por rol de planta) no se les exige contrato.
    numeroContrato: contratoAplica(f) && esCrear && conVacios && vacio(f.numeroContrato)
      // Al contratista no se le muestra la pregunta «¿Es funcionario?»: no se sugiere.
      ? rolSiempreContrato(f.rol)
        ? 'Ingrese el número de contrato.'
        : 'Ingrese el número de contrato o marque «¿Es funcionario?».'
      : null,
    fechaInicioContrato: contratoAplica(f) && esCrear && conVacios && !f.fechaInicioContrato
      ? 'Ingrese la fecha de inicio del contrato.' : null,
    fechaTerminacionContrato: !contratoAplica(f)
      ? null
      : f.fechaInicioContrato && f.fechaTerminacionContrato
        && f.fechaTerminacionContrato <= f.fechaInicioContrato
        ? 'Debe ser posterior a la fecha de inicio.'
        : esCrear && conVacios && !f.fechaTerminacionContrato
          ? 'Ingrese la fecha de terminación del contrato.' : null,
    // Plazo DIGITADO (sin cálculo automático): se exige al crear, al menos meses o días.
    plazoMeses: contratoAplica(f) && esCrear && conVacios
      && enteroPlazo(f.plazoMeses) === 0 && enteroPlazo(f.plazoDias) === 0
      ? 'Digite el plazo del contrato (meses y/o días).' : null,
    fechaTerminacionAdicion: !contratoAplica(f) || !f.tieneAdicion
      ? null
      : f.fechaTerminacionAdicion && f.fechaTerminacionContrato
        && f.fechaTerminacionAdicion <= f.fechaTerminacionContrato
        ? 'Debe ser posterior a la terminación original del contrato.'
        : conVacios && !f.fechaTerminacionAdicion
          ? 'Indique la nueva fecha de terminación por adición.' : null,
    adicionMeses: contratoAplica(f) && f.tieneAdicion && esCrear && conVacios
      && enteroPlazo(f.adicionMeses) === 0 && enteroPlazo(f.adicionDias) === 0
      ? 'Digite el plazo de la adición (meses y/o días).' : null,
  };
}

/** Orden de los campos en pantalla (para saltar al primero con error). */
export const ORDEN_CAMPOS_USUARIO = [
  'nombre', 'apellido', 'numeroDocumento', 'telefono', 'correo', 'password',
  'rol', 'area',
  'numeroContrato', 'fechaInicioContrato', 'fechaTerminacionContrato', 'plazoMeses',
  'fechaTerminacionAdicion', 'adicionMeses',
] as const;
